// src/components/staff-time/StaffStatus.jsx
import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Clock, Coffee, LogOut, AlertTriangle, Calendar } from 'lucide-react';
import { 
  checkIn, 
  checkOut, 
  startBreak, 
  endBreak, 
  getCurrentEntry 
} from '../../services/staffService';
import { 
  formatDuration,
  checkBreakRequirement 
} from '../../utils/helpers';
import { BREAK_TYPES} from '../../utils/constants';
import { styles } from '../../styles/theme';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import StaffAvailabilityTab from './StaffAvailabilityTab';

const StaffStatus = ({ staff, onLogout }) => {
  const [currentEntry, setCurrentEntry] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [hoursWorked, setHoursWorked] = useState(0);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [showSchedule, setShowSchedule] = useState(false);
  const [weekSchedules, setWeekSchedules] = useState([]);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [viewingWeek, setViewingWeek] = useState(0);
  const [activeTab, setActiveTab] = useState('checkin');
  const [breakElapsedMinutes, setBreakElapsedMinutes] = useState(0);
  const [todayShiftHours, setTodayShiftHours] = useState(null); // 'checkin' | 'availability'
  
  const isProcessingRef = useRef(false);

  // Fallback names for old hardcoded location ids
  const LEGACY_LOCATION_NAMES = {
    center: 'Stemtree Center',
    redmond: 'Stemtree Redmond',
    kirkland: 'Stemtree Kirkland',
    bca1: 'BCA Bellevue Campus 1',
    bca2: 'BCA Bellevue Campus 2',
  };

  const getLocationName = (locationId) => {
    const found = locations.find(l => l.id === locationId);
    if (found) return found.name;
    return LEGACY_LOCATION_NAMES[locationId] || locationId;
  };

  // Convert any location id or name to a stable lowercase key for storage
  const normalizeLocationKey = (locationId) => {
    const name = getLocationName(locationId).toLowerCase();
    if (name.includes('kirkland')) return 'kirkland';
    if (name.includes('redmond')) return 'redmond';
    if (name.includes('stemtree') || name.includes('center')) return 'center';
    if (name.includes('bca1') || name.includes('campus 1')) return 'bca1';
    if (name.includes('bca2') || name.includes('campus 2')) return 'bca2';
    if (name.includes('cascadia')) return 'cascadia';
    if (name.includes('bca')) return 'bca1';
    // Legacy keys passed directly
    if (['center','bca1','bca2','cascadia','redmond','kirkland'].includes(locationId.toLowerCase())) return locationId.toLowerCase();
    return name.replace(/\s+/g, '_').toLowerCase();
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      if (currentEntry && currentEntry.checkInTime) {
        const elapsed = (Date.now() - currentEntry.checkInTime.toMillis()) / (1000 * 60 * 60);
        setHoursWorked(elapsed);
      }
    }, 60000);
    return () => clearInterval(timer);
  }, [currentEntry]);

  useEffect(() => {
    loadCurrentEntry();
    loadLocations();
    loadTodayShift();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff.id]);

  const loadLocations = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'locations'));
      const list = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(l => l.active)
        .sort((a, b) => a.name.localeCompare(b.name));
      setLocations(list);
      if (list.length > 0) setSelectedLocation(list[0].id);
      else setSelectedLocation('redmond'); // fallback if no locations in Firestore
    } catch (err) {
      console.error('Error loading locations:', err);
    }
  };

  useEffect(() => {
    if (showSchedule) {
      loadWeekSchedule(viewingWeek);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSchedule, viewingWeek]);

  const loadCurrentEntry = async () => {
    try {
      const entry = await getCurrentEntry(staff.id);
      setCurrentEntry(entry);
      if (entry && entry.checkInTime) {
        const elapsed = (Date.now() - entry.checkInTime.toMillis()) / (1000 * 60 * 60);
        setHoursWorked(elapsed);
      }
    } catch (err) {
      console.error('Error loading current entry:', err);
    }
  };

  const loadWeekSchedule = async (weeksAhead = 0) => {
    setLoadingSchedule(true);
    try {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const monday = new Date(today);
      if (dayOfWeek === 0) {
        monday.setDate(today.getDate() + 1);
      } else {
        monday.setDate(today.getDate() - dayOfWeek + 1);
      }
      if (weeksAhead > 0) {
        monday.setDate(monday.getDate() + (weeksAhead * 7));
      }

      const formatDate = (date) => {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
      };

      const weekKey = formatDate(monday);

      // Check if this week's schedule is published
      const weekMetaSnap = await getDoc(doc(db, 'schedule_weeks', weekKey));
      const isPublished = weekMetaSnap.exists() && weekMetaSnap.data().published === true;

      const isPastWeek = weeksAhead < 0;

      if (!isPastWeek && !isPublished) {
        setWeekSchedules([{ _notPublished: true }]);
        setLoadingSchedule(false);
        return;
      }

      const weekDates = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(monday);
        date.setDate(monday.getDate() + i);
        weekDates.push(formatDate(date));
      }

      // Load active programs for validation (only needed for current/future weeks)
      let activePrograms = [];
      if (!isPastWeek) {
        const programsSnap = await getDocs(collection(db, 'programs'));
        activePrograms = programsSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.active !== false);
      }

      const normalize = (s) => (s || '').toLowerCase().replace(/[\s\-_]+/g, '');

      const isValidSchedule = (schedule) => {
        // Past weeks: show everything for payroll reference
        if (isPastWeek) return true;
        // New records with programId: always valid
        if (schedule.programId) return true;
        // Legacy records: validate against active programs
        return activePrograms.some(p => {
          const nameMatch =
            normalize(p.name) === normalize(schedule.programType) ||
            normalize(p.name).startsWith(normalize(schedule.programType)) ||
            normalize(schedule.programType).startsWith(normalize(p.name)) ||
            (p.legacyProgramTypes || []).some(t => normalize(t) === normalize(schedule.programType));
          const locationMatch =
            normalize(p.locationName || '').includes(normalize(schedule.location || '')) ||
            normalize(schedule.location || '').includes(normalize(p.locationName || ''));
          return nameMatch && locationMatch;
        });
      };

      const schedulesRef = collection(db, 'schedules');
      const q = query(schedulesRef, where('staffId', '==', staff.id));
      const snapshot = await getDocs(q);

      const mySchedules = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(s => weekDates.includes(s.date))
        .filter(isValidSchedule);

      const schedulesWithCoworkers = await Promise.all(
        mySchedules.map(async (schedule) => {
          const coworkersQuery = query(
            schedulesRef,
            where('date', '==', schedule.date),
            where('programType', '==', schedule.programType),
            where('location', '==', schedule.location)
          );
          const coworkersSnapshot = await getDocs(coworkersQuery);
          const coworkers = coworkersSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(s => s.staffId !== staff.id)
            .map(s => ({ name: s.staffName, time: `${s.startTime}-${s.endTime}` }));
          return { ...schedule, coworkers };
        })
      );

      setWeekSchedules(schedulesWithCoworkers);
    } catch (error) {
      console.error('Error loading schedule:', error);
    } finally {
      setLoadingSchedule(false);
    }
  };

  const loadTodayShift = async () => {
    try {
      const today = new Date();
      const m = String(today.getMonth() + 1).padStart(2, '0');
      const d = String(today.getDate()).padStart(2, '0');
      const y = today.getFullYear();
      const dateStr4 = m + '-' + d + '-' + y;           // MM-DD-YYYY
      const dateStr2 = m + '-' + d + '-' + String(y).slice(-2); // MM-DD-YY
      let snap = await getDocs(query(collection(db, 'schedules'), where('staffId', '==', staff.id), where('date', '==', dateStr4)));
      if (snap.empty) {
        snap = await getDocs(query(collection(db, 'schedules'), where('staffId', '==', staff.id), where('date', '==', dateStr2)));
      }
      if (!snap.empty) {
        const sched = snap.docs[0].data();
        const parseH = (t) => {
          if (!t) return null;
          const match = String(t).toUpperCase().match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/);
          if (!match) return null;
          let h = parseInt(match[1]), mn = parseInt(match[2]);
          if (match[3] === 'PM' && h !== 12) h += 12;
          if (match[3] === 'AM' && h === 12) h = 0;
          return h + mn / 60;
        };
        const start = parseH(sched.startTime);
        const end = parseH(sched.endTime);
        if (start !== null && end !== null) { setTodayShiftHours(end - start); return; }
      }
      setTodayShiftHours(8);
    } catch (err) { setTodayShiftHours(8); }
  };

  const getBreakCount = (breakType) => {
    if (!currentEntry || !currentEntry.breaks) return 0;
    return currentEntry.breaks.filter(b => b.type === breakType).length;
  };

  const handleCheckIn = async () => {
    if (isProcessingRef.current || isLoading) return;
    isProcessingRef.current = true;
    setIsLoading(true);
    setError('');
    try {
      await checkIn(staff.id, `${staff.firstName} ${staff.lastName}`, normalizeLocationKey(selectedLocation), null);
      await loadCurrentEntry();
      alert(`✅ Checked in at ${getLocationName(selectedLocation)}`);
    } catch (err) {
      console.error('Check-in error:', err);
      setError(err.message || 'Failed to check in. Please try again.');
    } finally {
      setIsLoading(false);
      setTimeout(() => { isProcessingRef.current = false; }, 1000);
    }
  };

  const handleCheckOut = async () => {
    if (!currentEntry) return;
    if (isProcessingRef.current || isLoading) return;
    const confirmed = window.confirm(`Check out now?\n\nHours worked: ${hoursWorked.toFixed(2)} hours`);
    if (!confirmed) return;
    isProcessingRef.current = true;
    setIsLoading(true);
    setError('');
    try {
      const result = await checkOut(currentEntry.id, currentEntry.checkInLocation, null);
      alert(`✅ Checked out!\n\nTotal Hours: ${result.totalHours.toFixed(2)}\nPaid Hours: ${result.paidHours.toFixed(2)}`);
      setCurrentEntry(null);
      setHoursWorked(0);
    } catch (err) {
      console.error('Check-out error:', err);
      setError(err.message || 'Failed to check out. Please try again.');
    } finally {
      setIsLoading(false);
      setTimeout(() => { isProcessingRef.current = false; }, 1000);
    }
  };

  const handleStartBreak = async (breakType) => {
    if (!currentEntry) return;
    if (isProcessingRef.current || isLoading) return;
    isProcessingRef.current = true;
    setIsLoading(true);
    setError('');
    try {
      await startBreak(currentEntry.id, breakType);
      await loadCurrentEntry();
      const breakName = breakType === BREAK_TYPES.PAID_REST ? '10-min paid break' : '30-min meal break';
      alert(`☕ ${breakName} started`);
    } catch (err) {
      console.error('Start break error:', err);
      setError(err.message || 'Failed to start break. Please try again.');
    } finally {
      setIsLoading(false);
      setTimeout(() => { isProcessingRef.current = false; }, 1000);
    }
  };

  const handleEndBreak = async () => {
    if (!currentEntry) return;
    if (isProcessingRef.current || isLoading) return;
    isProcessingRef.current = true;
    setIsLoading(true);
    setError('');
    try {
      const activeBreak = [...(currentEntry.breaks || [])].reverse().find(b => !b.endTime);
      const breakStartTime = activeBreak?.startTime;
      const wasLunch = activeBreak?.type === BREAK_TYPES.UNPAID_MEAL;
      const snapMs = Date.now();
      await endBreak(currentEntry.id);
      await loadCurrentEntry();
      if (breakStartTime) {
        const startMs = breakStartTime.toMillis ? breakStartTime.toMillis() : new Date(breakStartTime).getTime();
        const durationMins = Math.floor((snapMs - startMs) / 60000);
        const limit = wasLunch ? 32 : 12;
        const overBy = durationMins - limit;
        if (overBy > 0) {
          alert(`⚠️ ${wasLunch ? 'Lunch' : '10-Min'} break was ${durationMins} min — ${overBy} min over the limit.`);
        } else {
          alert(`✅ Break ended — ${durationMins} min. Back to work!`);
        }
      } else {
        alert('✅ Break ended. Back to work!');
      }
    } catch (err) {
      console.error('End break error:', err);
      setError(err.message || 'Failed to end break. Please try again.');
    } finally {
      setIsLoading(false);
      setTimeout(() => { isProcessingRef.current = false; }, 1000);
    }
  };

  const breakStatus = currentEntry ? checkBreakRequirement(hoursWorked, currentEntry.breaks || []) : null;
  const paidBreaksTaken = getBreakCount(BREAK_TYPES.PAID_REST);
  const maxPaidBreaksReached = paidBreaksTaken >= 2;
  const mealBreakTaken = getBreakCount(BREAK_TYPES.UNPAID_MEAL) >= 1;
  const activeBreakType = currentEntry?.status === 'on-break'
    ? ([...(currentEntry.breaks || [])].reverse().find(b => !b.endTime)?.type || '')
    : null;
  const breakLabel = activeBreakType === 'unpaid-meal' ? '🍽️ Lunch Break' : '☕️ 10-Min Break';
  const breakOverLimit = activeBreakType === 'unpaid-meal' ? breakElapsedMinutes > 35 : breakElapsedMinutes > 12;
  const isStemtreeLocation = currentEntry && !getLocationName(currentEntry.checkInLocation).toLowerCase().match(/bca|school|cascadia/);
  const missingLunchWarning = isStemtreeLocation && hoursWorked >= 5 && !mealBreakTaken && currentEntry?.status !== 'on-break';


  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>

      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
        padding: '1.5rem 0 0',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      }}>
        {/* Top row: name + logout */}
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
          padding: '0 1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '1.25rem',
        }}>
          <div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: 'white', margin: '0 0 0.25rem' }}>
              Welcome, {staff.firstName}!
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem', margin: 0 }}>
              {staff.role} • {currentTime.toLocaleTimeString()}
            </p>
          </div>
          <button
            onClick={onLogout}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '2px solid white',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
            }}
          >
            <LogOut style={{ width: '15px', height: '15px' }} />
            Logout
          </button>
        </div>

        {/* Tab Bar */}
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
          padding: '0 1rem',
          display: 'flex',
          gap: '0.25rem',
        }}>
          {[
            { id: 'checkin', label: '⏱ Check In / Out' },
            { id: 'availability', label: '📅 My Availability' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: '0.65rem 0.5rem',
                border: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === tab.id ? '#f8fafc' : 'rgba(255,255,255,0.15)',
                color: activeTab === tab.id ? '#1e40af' : 'white',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab Content ── */}

      {activeTab === 'availability' ? (
        <StaffAvailabilityTab staff={staff} />
      ) : (
        // ── Check In / Out Tab ──
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '1.5rem 1rem' }}>

          {/* Error */}
          {error && (
            <div style={{
              ...styles.card,
              backgroundColor: '#fef2f2',
              border: '2px solid #fecaca',
              marginBottom: '1.5rem',
              display: 'flex',
              gap: '0.75rem',
            }}>
              <AlertTriangle style={{ width: '24px', height: '24px', color: '#dc2626', flexShrink: 0 }} />
              <p style={{ margin: 0, color: '#dc2626', fontSize: '0.875rem' }}>{error}</p>
            </div>
          )}

          {/* Current Status Card */}
          {currentEntry && (
            <div style={{ ...styles.card, marginBottom: '1.5rem' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
              }}>
                <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 'bold' }}>
                  {currentEntry.status === 'checked-in' ? '✅ Checked In' : '☕ On Break'}
                </h2>
                <span style={{
                  padding: '0.25rem 0.75rem',
                  backgroundColor: currentEntry.status === 'checked-in' ? '#d1fae5' : '#fef3c7',
                  color: currentEntry.status === 'checked-in' ? '#065f46' : '#92400e',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                }}>
                  {formatDuration(hoursWorked * 60)}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem', color: '#374151' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Clock style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                  <span>Checked in at {currentEntry.checkInTime.toDate().toLocaleTimeString()}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <MapPin style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                  <span>{getLocationName(currentEntry.checkInLocation)}</span>
                </div>
                {currentEntry.breaks && currentEntry.breaks.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Coffee style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                    <span>{currentEntry.breaks.length} break(s) taken</span>
                  </div>
                )}
              </div>

              {getLocationName(currentEntry.checkInLocation).toLowerCase().includes('center') && breakStatus && breakStatus.needsRestBreak && paidBreaksTaken === 0 && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fcd34d',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  color: '#92400e',
                }}>
                  ⚠️ <strong>Break Required:</strong> You've worked 4+ hours. Please take your 10-minute paid break.
                </div>
              )}
            </div>
          )}

          {/* My Schedule Toggle */}
          <div style={{ marginBottom: '1.5rem' }}>
            <button
              onClick={() => setShowSchedule(!showSchedule)}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: showSchedule ? '#eff6ff' : 'white',
                color: showSchedule ? '#1e40af' : '#374151',
                border: `2px solid ${showSchedule ? '#3b82f6' : '#e5e7eb'}`,
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                transition: 'all 0.15s',
              }}
            >
              <Calendar style={{ width: '16px', height: '16px' }} />
              {showSchedule ? 'Hide My Schedule' : '📋 View My Schedule'}
            </button>
            <div style={{ textAlign: 'center', fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.3rem' }}>
              Week runs Sunday – Saturday
            </div>
          </div>

          {/* Weekly Schedule Section */}
          {showSchedule && (
            <div style={{ ...styles.card, marginBottom: '1.5rem' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
                flexWrap: 'wrap',
                gap: '0.5rem',
              }}>
                <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Calendar style={{ width: '20px', height: '20px' }} />
                  {viewingWeek === 0 ? 'This Week' : viewingWeek === 1 ? 'Next Week' : `+${viewingWeek} Weeks`}
                </h2>
                {/* Week tabs — always show current + 3 weeks ahead */}
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {[0, 1, 2, 3].map(offset => {
                    const label = offset === 0 ? 'This Week' : offset === 1 ? 'Next Week' : `+${offset} Weeks`;
                    return (
                      <button
                        key={offset}
                        onClick={() => setViewingWeek(offset)}
                        style={{
                          padding: '0.4rem 0.8rem',
                          fontSize: '0.8rem',
                          backgroundColor: viewingWeek === offset ? '#3b82f6' : '#e5e7eb',
                          color: viewingWeek === offset ? 'white' : '#374151',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '600',
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {loadingSchedule ? (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>Loading schedule...</p>
              ) : weekSchedules[0]?._notPublished ? (
                <div style={{ textAlign: 'center', padding: '2rem', backgroundColor: '#fffbeb', borderRadius: '8px', margin: '1rem', border: '1px solid #fde68a' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📝</div>
                  <p style={{ color: '#92400e', fontWeight: '600', margin: 0 }}>Schedule not yet published</p>
                  <p style={{ color: '#9ca3af', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>Check back soon — your manager is still working on it.</p>
                </div>
              ) : weekSchedules.length === 0 ? (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>No schedules for this week</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {weekSchedules
                    .sort((a, b) => a.date.localeCompare(b.date))
                    .map((schedule, idx) => {
                      const [month, day, year] = schedule.date.split('-');
                      const scheduleDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                      const isToday = scheduleDate.toDateString() === new Date().toDateString();
                      return (
                        <div key={idx} style={{
                          padding: '1rem',
                          backgroundColor: isToday ? '#dbeafe' : '#f9fafb',
                          border: isToday ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                          borderRadius: '8px',
                        }}>
                          <div style={{ fontWeight: 'bold', color: isToday ? '#1e40af' : '#1e293b', marginBottom: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <span>
                              {schedule.dayOfWeek}, {scheduleDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              {isToday && ' 📍'}
                            </span>
                            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              {schedule.startTime} - {schedule.endTime}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#4b5563', marginBottom: '0.5rem' }}>
                            🎪 <strong>{schedule.programType}</strong> at {schedule.location}
                          </div>
                          {schedule.notes && (
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic', marginBottom: '0.5rem', padding: '0.5rem', backgroundColor: 'white', borderRadius: '4px' }}>
                              📝 {schedule.notes}
                            </div>
                          )}
                          {schedule.coworkers && schedule.coworkers.length > 0 && (
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #e5e7eb' }}>
                              <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>👥 Other staff scheduled today:</div>
                              {schedule.coworkers.map((coworker, cIdx) => (
                                <div key={cIdx} style={{ marginLeft: '1rem', marginTop: '0.25rem' }}>
                                  • {coworker.name} <span style={{ color: '#9ca3af' }}>({coworker.time})</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {!currentEntry ? (
              <>
                <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                  Select Location:
                </label>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  disabled={isLoading}
                  style={{ ...styles.input, marginBottom: '0.75rem', opacity: isLoading ? 0.6 : 1 }}
                >
                  {locations.length > 0 ? locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  )) : (
                    <>
                      <option value="redmond">📍 Stemtree Redmond</option>
                      <option value="kirkland">📍 Stemtree Kirkland</option>
                      <option value="center">🏢 Center</option>
                      <option value="school">🏫 School</option>
                    </>
                  )}
                </select>
                <button
                  onClick={handleCheckIn}
                  disabled={isLoading}
                  style={{
                    ...styles.button,
                    padding: '1.25rem',
                    fontSize: '1.125rem',
                    fontWeight: 'bold',
                    backgroundColor: isLoading ? '#9ca3af' : '#10b981',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: 1,
                  }}
                >
                  {isLoading ? '⏳ Checking In...' : '✅ Check In'}
                </button>
              </>
            ) : currentEntry.status === 'on-break' ? (
              <button
                onClick={handleEndBreak}
                disabled={isLoading}
                style={{
                  ...styles.button,
                  padding: '1.25rem',
                  fontSize: '1.125rem',
                  fontWeight: 'bold',
                  backgroundColor: isLoading ? '#9ca3af' : '#10b981',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: 1,
                }}
              >
                {isLoading ? '⏳ Processing...' : '✅ End Break'}
              </button>
            ) : (
              <>
                {/* Break buttons — all visible, greyed when taken, lunch/break2 hidden for half shift */}
                {(() => {
                  const locName = getLocationName(currentEntry.checkInLocation).toLowerCase();
                  const isSchool = locName.includes('bca') || locName.includes('school') || locName.includes('cascadia');
                  if (isSchool) {
                    return (
                      <div style={{ padding: '1rem', backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '8px', fontSize: '0.875rem', color: '#1e40af', marginBottom: '0.75rem' }}>
                        ℹ️ <strong>ASP - Outcenter Program</strong>
                      </div>
                    );
                  }
                  const isFullShift = todayShiftHours === null || todayShiftHours > 4;
                  const break1Taken = paidBreaksTaken >= 1;
                  const break2Taken = paidBreaksTaken >= 2;
                  return (
                    <>
                      {todayShiftHours === null && (
                        <div style={{ fontSize: '0.72rem', color: '#6b7280', backgroundColor: '#f3f4f6', padding: '0.4rem 0.6rem', borderRadius: '5px', marginBottom: '0.5rem' }}>
                          ℹ️ Not on today's schedule — showing full shift breaks.
                        </div>
                      )}
                      <button
                        onClick={() => !break1Taken && handleStartBreak(BREAK_TYPES.PAID_REST)}
                        disabled={isLoading || break1Taken}
                        style={{ ...styles.button, padding: '1rem', fontSize: '1rem', marginBottom: '0.5rem', opacity: 1,
                          backgroundColor: break1Taken ? '#d1fae5' : (isLoading ? '#9ca3af' : '#f59e0b'),
                          color: break1Taken ? '#065f46' : 'white',
                          cursor: (isLoading || break1Taken) ? 'default' : 'pointer' }}
                      >
                        {break1Taken ? '✓ 10-Min Break Taken' : (isLoading ? '⏳ Processing...' : '☕ 10-Min Break (Paid)')}
                      </button>
                      {isFullShift && (
                        <button
                          onClick={() => !mealBreakTaken && handleStartBreak(BREAK_TYPES.UNPAID_MEAL)}
                          disabled={isLoading || mealBreakTaken}
                          style={{ ...styles.button, padding: '1rem', fontSize: '1rem', marginBottom: '0.5rem', opacity: 1,
                            backgroundColor: mealBreakTaken ? '#d1fae5' : (isLoading ? '#9ca3af' : '#8b5cf6'),
                            color: mealBreakTaken ? '#065f46' : 'white',
                            cursor: (isLoading || mealBreakTaken) ? 'default' : 'pointer' }}
                        >
                          {mealBreakTaken ? '✓ Lunch Break Taken' : (isLoading ? '⏳ Processing...' : '🍽️ Lunch Break (30 min)')}
                        </button>
                      )}
                      {isFullShift && (
                        <button
                          onClick={() => !break2Taken && handleStartBreak(BREAK_TYPES.PAID_REST)}
                          disabled={isLoading || break2Taken}
                          style={{ ...styles.button, padding: '1rem', fontSize: '1rem', marginBottom: '0.5rem', opacity: 1,
                            backgroundColor: break2Taken ? '#d1fae5' : (isLoading ? '#9ca3af' : '#f59e0b'),
                            color: break2Taken ? '#065f46' : 'white',
                            cursor: (isLoading || break2Taken) ? 'default' : 'pointer' }}
                        >
                          {break2Taken ? '✓ 10-Min Break 2 Taken' : (isLoading ? '⏳ Processing...' : '☕ 10-Min Break 2 (Paid)')}
                        </button>
                      )}
                      {!isFullShift && break1Taken && (
                        <div style={{ padding: '0.75rem', backgroundColor: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '8px', fontSize: '0.875rem', color: '#065f46', textAlign: 'center', fontWeight: '600' }}>
                          ✅ Break taken for today
                        </div>
                      )}
                    </>
                  );
                })()}
                <button
                  onClick={handleCheckOut}
                  disabled={isLoading}
                  style={{
                    ...styles.button,
                    padding: '1.25rem',
                    fontSize: '1.125rem',
                    fontWeight: 'bold',
                    backgroundColor: isLoading ? '#9ca3af' : '#ef4444',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: 1,
                    marginTop: getLocationName(currentEntry.checkInLocation).toLowerCase().includes('center') ? '1rem' : '0',
                  }}
                >
                  {isLoading ? '⏳ Processing...' : '🚪 Check Out'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffStatus;